import { Radio, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEvent } from '../context/EventContext';
import {
  FOLLOW_COUNTDOWN_SECONDS,
  countdownSeconds,
  detectFollowChange,
  topicPath,
} from '../lib/presenterFollow';
import type { EventSettings } from '../types';

interface PromptState {
  topicId: string;
  deferredForDraft: boolean;
  deadline: number | null;
}

export function NowPresenting() {
  const {
    snapshot,
    followPresenter,
    setFollowPresenter,
    hasCommentDraft,
  } = useEvent();
  const location = useLocation();
  const navigate = useNavigate();
  const previousSettings = useRef<EventSettings | null>(null);
  const [prompt, setPrompt] = useState<PromptState | null>(null);
  const [seconds, setSeconds] = useState(FOLLOW_COUNTDOWN_SECONDS);

  useEffect(() => {
    if (!snapshot) return;
    const change = detectFollowChange(previousSettings.current, snapshot.settings, {
      pathname: location.pathname,
      localFollowEnabled: followPresenter,
      hasCommentDraft,
    });
    previousSettings.current = snapshot.settings;
    if (!change) return;

    const deadline = change.deferredForDraft
      ? null
      : Date.now() + FOLLOW_COUNTDOWN_SECONDS * 1_000;
    setSeconds(FOLLOW_COUNTDOWN_SECONDS);
    setPrompt({ ...change, deadline });
  }, [
    followPresenter,
    hasCommentDraft,
    location.pathname,
    snapshot,
  ]);

  useEffect(() => {
    if (!prompt?.deadline || prompt.deferredForDraft) return;
    const tick = () => {
      const next = countdownSeconds(prompt.deadline ?? Date.now(), Date.now());
      setSeconds(next);
      if (next === 0) {
        setPrompt(null);
        navigate(topicPath(prompt.topicId));
      }
    };
    tick();
    const timer = window.setInterval(tick, 250);
    return () => window.clearInterval(timer);
  }, [navigate, prompt]);

  useEffect(() => {
    if (!prompt || !hasCommentDraft || prompt.deferredForDraft) return;
    setPrompt({ ...prompt, deferredForDraft: true, deadline: null });
  }, [hasCommentDraft, prompt]);

  useEffect(() => {
    if (
      !followPresenter ||
      !snapshot?.settings.audienceFollowEnabled ||
      !snapshot.settings.currentTopicId
    ) {
      setPrompt(null);
    }
  }, [
    followPresenter,
    snapshot?.settings.audienceFollowEnabled,
    snapshot?.settings.currentTopicId,
  ]);

  const topic = snapshot?.topics.find((item) => item.id === prompt?.topicId);
  if (!prompt || !topic) return null;

  const joinNow = () => {
    setPrompt(null);
    navigate(topicPath(topic.id));
  };

  return (
    <aside className="now-presenting" aria-live="polite" aria-atomic="true">
      <Radio aria-hidden="true" />
      <div>
        <strong>Now presenting: {topic.title}</strong>
        <span>
          {prompt.deferredForDraft
            ? 'Your comment draft is safe. Join when you are ready.'
            : `Opening in ${seconds} second${seconds === 1 ? '' : 's'}.`}
        </span>
      </div>
      <button className="button primary" type="button" onClick={joinNow}>
        Join now
      </button>
      <button
        className="dismiss-button"
        type="button"
        aria-label="Stay on this page and stop following the presenter"
        onClick={() => {
          setFollowPresenter(false);
          setPrompt(null);
        }}
      >
        <X aria-hidden="true" />
      </button>
    </aside>
  );
}
